import { useCallback, useEffect, useRef } from "react";

import HCaptcha from "@hcaptcha/react-hcaptcha";
import { STRING } from "../SchemaTypes";
import { useFormatMessage } from "../../Intl/Context";
import { useForm } from "../withFormStore";
import { useDispatch, useSelector } from "react-redux";
import { defineMessages } from "react-intl";

import { updateFieldValue } from "../../../reducers/form/fields";
import { resetCaptcha } from "../../../reducers/captcha";
import Field from "../Field";
import { useParsedFieldProps } from "./useParsedFieldProps";

export const useCaptcha = (fieldName = "captcha") => {
  const { formKey } = useForm();
  const dispatch = useDispatch();

  const captchaTimestamp = useSelector(({ captcha }) => captcha.timestamp);

  const resetCaptchaCallback = useCallback(() => {
    // Update the captcha timestamp which will force refreshing the captcha image with
    // a valid one if the captcha code is currently displayed.
    dispatch(resetCaptcha());

    // Also empty the captcha field to ensure the user cannot submit a new form without
    // having updated this field, which value cannot be the same after a submission
    // failure
    dispatch(updateFieldValue(formKey, fieldName, { value: "" }));
  }, [dispatch, fieldName, formKey]);

  return [captchaTimestamp, resetCaptchaCallback];
};

const CaptchaField = props => {
  const { fieldProps } = useParsedFieldProps(props);
  const { required } = fieldProps;
  const { fieldName, parent, sitekey, resetCaptcha } = props;

  const schema = {
    type: STRING,
    parent,
    validations: {
      required
    }
  };

  const t = useFormatMessage();
  const label = props.label || t(intlMessages.captcha);

  const { formKey } = useForm();
  const dispatch = useDispatch();

  const handleCaptchaVerification = useCallback(
    token => {
      // Update the captcha field value with the received token
      dispatch(updateFieldValue(formKey, fieldName, { value: token }));
    },
    [dispatch, fieldName, formKey]
  );

  const captchaTimestamp = useSelector(state => state.captcha.timestamp);
  const hCaptchaRef = useRef();

  useEffect(() => {
    if (hCaptchaRef.current) {
      hCaptchaRef.current.resetCaptcha();
    }
  }, [captchaTimestamp]);

  return (
    <Field {...fieldProps} label={label} schema={schema}>
      <HCaptcha
        ref={hCaptchaRef}
        sitekey={sitekey}
        onVerify={handleCaptchaVerification}
        onExpire={resetCaptcha}
      />
    </Field>
  );
};

export default CaptchaField;

const intlMessages = defineMessages({
  captcha: {
    id: "authentication.signIn.captcha",
    defaultMessage: "Code de vérification"
  },
  captchaInputPlaceholder: {
    id: "authentication.signIn.captchaInputPlaceholder",
    defaultMessage: "Renseigez le contenu de l'image ci-dessus"
  }
});
